import { useMoney } from '../hooks/useMoney';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  DataTable,
  Spinner,
  Select,
  Thumbnail,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface SlowMovingItem {
  product_id: number;
  sku: string;
  title: string;
  vendor: string | null;
  featured_image_url: string | null;
  cost_price: number;
  stock_quantity: number;
  available_quantity: number;
  stock_value: number;
  units_sold_in_period: number;
  last_sale_date: string;
  days_of_stock: number;
  status: 'No Sales' | 'Overstock' | 'Slow' | 'Normal';
}

interface Summary {
  total_products_with_stock: number;
  total_units: number;
  total_value: number;
  no_sales_products: number;
  overstock_products: number;
  slow_moving_products: number;
}

export default function SlowMovingReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [data, setData] = useState<SlowMovingItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [days, setDays] = useState('90');
  const [minStock, setMinStock] = useState('1');

  const loadReport = useCallback(async () => {
    const params = new URLSearchParams({
      days,
      min_stock: minStock,
    });

    const result = await fetchApi<{
      success: boolean;
      data: SlowMovingItem[];
      summary: Summary;
    }>(`/reports/slow-moving?${params.toString()}`);

    if (result?.success) {
      setData(result.data || []);
      setSummary(result.summary);
    }
  }, [fetchApi, days, minStock]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; download_url: string }>('/reports/slow-moving/export', {
        method: 'POST',
        body: JSON.stringify({ days: parseInt(days), min_stock: parseInt(minStock) }),
      });

      if (result?.success && result.download_url) {
        window.open(`${import.meta.env.VITE_API_URL}${result.download_url}`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = useMoney();

  const getStatusBadge = (status: string) => {
    const config: Record<string, { tone: 'critical' | 'warning' | 'info' | 'success'; label: string }> = {
      'No Sales': { tone: 'critical', label: 'No Sales' },
      'Overstock': { tone: 'warning', label: 'Overstock' },
      'Slow': { tone: 'info', label: 'Slow Moving' },
      'Normal': { tone: 'success', label: 'Normal' },
    };
    const { tone, label } = config[status] || { tone: 'info', label: status };
    return <Badge tone={tone}>{label}</Badge>;
  };

  const tableRows = data.map((item) => [
    <InlineStack gap="300" align="start" wrap={false}>
      {item.featured_image_url ? (
        <Thumbnail source={item.featured_image_url} alt={item.title} size="small" />
      ) : (
        <div style={{ width: 40, height: 40, background: '#f4f6f8', borderRadius: 4 }} />
      )}
      <BlockStack gap="050">
        <Button variant="plain" onClick={() => navigate(`/products/${item.product_id}`)}>
          {item.sku || '-'}
        </Button>
        <Text as="span" variant="bodySm" tone="subdued">{item.title}</Text>
      </BlockStack>
    </InlineStack>,
    item.vendor || '-',
    String(item.stock_quantity),
    formatCurrency(item.stock_value),
    String(item.units_sold_in_period),
    item.last_sale_date === 'Never' ? (
      <Badge tone="critical">Never</Badge>
    ) : (
      new Date(item.last_sale_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    ),
    item.days_of_stock > 0 ? `${item.days_of_stock} days` : '-',
    getStatusBadge(item.status),
  ]);

  return (
    <Page
      title="Slow Moving Stock Report"
      subtitle="Identify products with low turnover to optimize inventory"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: data.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Total Stock Value</Text>
                  <Text as="p" variant="heading2xl">{formatCurrency(summary.total_value)}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">No Sales</Text>
                  <Text as="p" variant="heading2xl" tone="critical">{summary.no_sales_products}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Overstock</Text>
                  <Text as="p" variant="heading2xl">{summary.overstock_products}</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Slow Moving</Text>
                  <Text as="p" variant="heading2xl">{summary.slow_moving_products}</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Info Banner */}
        <Layout.Section>
          <Banner tone="info">
            <p><strong>Status Definitions:</strong></p>
            <ul style={{ margin: '8px 0 0 16px' }}>
              <li><strong>No Sales:</strong> Zero units sold in the analysis period</li>
              <li><strong>Overstock:</strong> Current stock will last more than 180 days at current sales rate</li>
              <li><strong>Slow Moving:</strong> Stock will last 90-180 days at current sales rate</li>
            </ul>
          </Banner>
        </Layout.Section>

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start" wrap>
              <div style={{ width: '180px' }}>
                <Select
                  label="Analysis Period"
                  options={[
                    { label: 'Last 30 days', value: '30' },
                    { label: 'Last 60 days', value: '60' },
                    { label: 'Last 90 days', value: '90' },
                    { label: 'Last 180 days', value: '180' },
                    { label: 'Last 365 days', value: '365' },
                  ]}
                  value={days}
                  onChange={setDays}
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  label="Min Stock Qty"
                  options={[
                    { label: 'Any stock', value: '1' },
                    { label: '5+ units', value: '5' },
                    { label: '10+ units', value: '10' },
                    { label: '25+ units', value: '25' },
                    { label: '50+ units', value: '50' },
                  ]}
                  value={minStock}
                  onChange={setMinStock}
                />
              </div>
              <div style={{ paddingTop: '24px' }}>
                <Button onClick={loadReport}>Refresh</Button>
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Data Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Slow Moving Products ({data.length})
              </Text>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : data.length === 0 ? (
                <Banner tone="success">
                  No slow moving stock found. All products have healthy turnover rates.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text', 'text']}
                  headings={['Product', 'Vendor', 'Stock', 'Value', `Sold (${days}d)`, 'Last Sale', 'Days Supply', 'Status']}
                  rows={tableRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
